import { styled } from '@mui/material/styles';

export type BoxMarginProps = {
    marginLeft?: number | 'auto';
    marginRight?: number | 'auto';
    marginBottom?: number | 'auto';
    marginTop?: number | 'auto';
    margin?: number | 'auto';
};

type StepsBaseStyleProps = {
    variant: 'vertical' | 'horizontal';
    childCount: number;
} & BoxMarginProps;

// BoxMargin styled component
export const BoxMargin = styled('div', {
    shouldForwardProp: (prop) =>
        ![
            'marginLeft',
            'marginTop',
            'marginRight',
            'marginBottom',
            'margin',
        ].includes(prop as string),
})<BoxMarginProps>(
    ({ marginLeft, marginTop, marginRight, marginBottom, margin }) => ({
        marginLeft: marginLeft ?? margin,
        marginTop: marginTop ?? margin,
        marginRight: marginRight ?? margin,
        marginBottom: marginBottom ?? margin,
    }),
);

// Steps root styled component
export const StepsRoot = styled(BoxMargin, {
    shouldForwardProp: (prop) =>
        !['variant', 'childCount'].includes(prop as string),
})<StepsBaseStyleProps>(({ variant, childCount }) => ({
    display: 'flex',
    flexDirection: variant === 'vertical' ? 'column' : 'row',
    justifyContent: variant === 'vertical' ? 'center' : 'initial',
    alignItems: 'center',
    width: variant === 'vertical' ? 40 : childCount * 40,
    height: variant === 'vertical' ? 'auto' : 40,
}));

export const StepOuterDefault = styled('div')(({ theme }) => ({
    width: 40,
    height: 40,
    backgroundColor: theme.legacy.colors?.neutralHoverGrey || '#F6F6F6',
    boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
}));

export const StepInnerDefault = styled('button')(({ theme }) => ({
    width: 32,
    height: 32,
    backgroundColor: theme.legacy.colors?.neutralPlatinum || '#E3E3E3',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: theme.typography.body1.font,
    fontSize: 14,
    lineHeight: '13px',
    fontWeight: 700,
    borderStyle: 'none',
    color: theme.legacy.colors?.primaryWhite || '#FFFFFF',
    '&:disabled': {
        backgroundColor: theme.legacy.colors?.neutralPlatinum || '#E3E3E3',
        color: theme.legacy.colors?.primaryWhite || '#FFFFFF',
        cursor: 'initial',
    },
}));

export const StepOuterActive = styled(StepOuterDefault)(({ theme }) => ({
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.legacy.colors?.primary || theme.palette.primary.main,
}));

export const StepInnerActive = styled(StepInnerDefault)(({ theme }) => ({
    cursor: 'pointer',
    padding: 0,
    outline: 'none',
    backgroundColor: theme.legacy.colors
        ? theme.legacy.colors.primary
        : theme.palette.primary.main,
    color: theme.legacy.colors
        ? theme.legacy.colors.primaryWhite
        : theme.palette.primary.contrastText,
    '&:hover': {
        backgroundColor: theme.legacy.colors
            ? theme.legacy.colors.accentDarkBlue
            : theme.palette.primary.dark,
    },
    '&:active': {
        backgroundColor: theme.legacy.colors
            ? theme.legacy.colors.neutralPlatinum
            : theme.palette.action.disabledBackground,
        color: theme.legacy.colors
            ? theme.legacy.colors.neutralGrey75
            : theme.palette.action.disabled,
    },
    '&:disabled': {
        backgroundColor: theme.legacy.colors
            ? theme.legacy.colors.primary
            : theme.palette.primary.main,
        color: theme.legacy.colors
            ? theme.legacy.colors.primaryWhite
            : theme.palette.primary.contrastText,
        cursor: 'initial',
    },
}));

export const StepLineDefault = styled('div', {
    shouldForwardProp: (prop) => prop !== 'variant',
})<{
    variant: 'vertical' | 'horizontal';
}>(({ theme, variant }) => ({
    width: variant === 'vertical' ? 2 : 40,
    height: variant === 'vertical' ? 40 : 2,
    backgroundColor: theme.legacy.colors?.neutralPlatinum || '#E3E3E3',
}));

export const StepLineActive = styled(StepLineDefault)(({ theme }) => ({
    backgroundColor: theme.legacy.colors?.primary || theme.palette.primary.main,
}));
